import React, { useState, useMemo } from "react";

import { cn } from "@/lib/utils";
import { MetaFieldEditModal } from "../../../MetaFieldEditModal";
import { CARD } from "@/config/design-tokens";
import type { ShopifySectionWidgetProps } from "../types";
import { useLanguage } from "@/i18n/useLanguage";
import { getUITranslation } from "@/lib/views/labels/translations";

type AddressType = "billing" | "shipping";

interface AddressSectionProps extends ShopifySectionWidgetProps {
  addressType: AddressType;
}

/**
 * Address Section Widget
 * 
 * Renders billing or shipping address with HTML support.
 * Addresses from ERPNext often contain HTML (e.g., <br> tags).
 */
export function AddressSection({
  section,
  formData,
  onFieldChange,
  disabled,
  onSave,
  isSaving,
  addressType,
}: AddressSectionProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const { language } = useLanguage();
  
  // Determine fieldname based on address type
  const fieldname = addressType === "billing" ? "address_display" : "shipping_address";
  const value = formData[fieldname];

  const noAddressSetLabel = useMemo(() => {
    const key = addressType === "billing" ? "no_billing_address_set" : "no_shipping_address_set";
    return getUITranslation(key, language) || `No ${addressType} address set`;
  }, [addressType, language]);

  return (
    <>
      <div className="space-y-2">
        <div
          className={cn(
            CARD,
            !disabled && "cursor-pointer",
            disabled && "cursor-not-allowed"
          )}
          onClick={() => !disabled && setModalOpen(true)}
        >
          <div className="p-5">
            {value ? (
              <div 
                className="text-sm font-medium text-foreground leading-relaxed"
                dangerouslySetInnerHTML={{ __html: value }}
              />
            ) : (
              <div className="text-sm text-muted-foreground italic">
                {noAddressSetLabel}
              </div>
            )}
          </div>
        </div>
      </div>

      <MetaFieldEditModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        section={section}
        formData={formData}
        onFieldChange={onFieldChange}
        onSave={onSave}
        disabled={disabled}
        isSaving={isSaving}
      />
    </>
  );
}

/**
 * Billing Address Section Wrapper
 * 
 * Wrapper component for billing address to maintain clean component identity.
 */
export const BillingAddressSection: React.FC<ShopifySectionWidgetProps> = (props) => (
  <AddressSection {...props} addressType="billing" />
);

/**
 * Shipping Address Section Wrapper
 * 
 * Wrapper component for shipping address to maintain clean component identity.
 */
export const ShippingAddressSection: React.FC<ShopifySectionWidgetProps> = (props) => (
  <AddressSection {...props} addressType="shipping" />
);


